import { render, nothing } from 'lit-html';
import flatpickr from 'flatpickr';
import {
  cartHeaderFiltersTemplate,
} from 'b2b-plus/ui-templates';
import { formatMoney } from 'b2b-plus/ui';
import {
  parseISODateLocal,
  toISODate,
  startOfLocalDay,
  shipWindowEndBounds,
  getShipWindowLengths,
  isSingleDayShipWindow,
  atsImmediatePickerEnd,
  getAtsImmediatePickerHorizonDays,
  isDeliveryWindowPast,
} from 'b2b-plus/inventory';
import {
  displayDateFlatpickrOptions,
  formatDisplayWindowString,
  formatDeliveryWindowDisplay,
} from 'b2b-plus/product-table-sdk';

class B2BPlusCartHeader extends HTMLElement {
  /** @type {object|null} flatpickr instance for the multi-apply date input. */
  #multiApplyFp = null;

  // Stored init params so updateDateTabs can reconstruct a full init when needed.
  #inventoryDateRange = null;
  #seasons = [];
  #types   = [];

  /** True while delegated host listeners are registered (cleared on disconnect). */
  #eventsConnected = false;

  /** Delegated click: date tabs, clear filters, multi-apply. */
  #onDelegatedClick = (e) => {
    const tab = e.target.closest('[data-tab]');
    if (tab) {
      this.querySelectorAll('[data-tab]').forEach((btn) => btn.classList.remove('is-active'));
      tab.classList.add('is-active');
      this.#dispatchFilterChange();
      return;
    }

    const clearBtn = e.target.closest('[data-clear-filters]');
    if (clearBtn) {
      this.querySelectorAll('select:not([data-multi-apply-scope])').forEach((s) => { s.value = 'all'; });
      this.#updateClearFilterVisibility();
      this.#dispatchFilterChange();
      return;
    }

    const applyBtn = e.target.closest('[data-multi-apply-btn]');
    if (applyBtn) {
      const dateInput = this.querySelector('[data-multi-apply-date]');
      const start = dateInput?.dataset.selectedStart;
      const end   = dateInput?.dataset.selectedEnd;
      const scope = this.querySelector('[data-multi-apply-scope]')?.value ?? 'all';
      if (!start || !end) return;
      // Reset filters before dispatching so when the event handler clicks the "all" tab
      // and fires b2b:cart-filter, the selects already read 'all'.
      this.querySelectorAll('select:not([data-multi-apply-scope])').forEach((s) => { s.value = 'all'; });
      this.#updateClearFilterVisibility();
      this.dispatchEvent(new CustomEvent('b2b:multi-apply-date', {
        bubbles: true,
        detail: { start, end, scope },
      }));
    }
  };

  /** Delegated change: season / type selects. */
  #onDelegatedChange = (e) => {
    if (e.target.closest('select:not([data-multi-apply-scope])')) {
      this.#updateClearFilterVisibility();
      this.#dispatchFilterChange();
    }
  };

  connectedCallback() {
    if (this.#eventsConnected) return;
    this.#eventsConnected = true;
    this.addEventListener('click', this.#onDelegatedClick);
    this.addEventListener('change', this.#onDelegatedChange);
  }

  disconnectedCallback() {
    this.#destroyMultiApplyFp();
    if (this.#eventsConnected) {
      this.removeEventListener('click', this.#onDelegatedClick);
      this.removeEventListener('change', this.#onDelegatedChange);
      this.#eventsConnected = false;
    }
  }

  /**
   * Populate and render the filter bar. Called by b2b-plus-cart after all data is loaded.
   * @param {object}      options
   * @param {Set<string>} options.dates             - Unique requested_ship_date values from IndexedDB cart
   * @param {string[]}    options.seasons           - Unique season values from inventory data
   * @param {string[]}    options.types             - Unique order type values from inventory data
   * @param {Map<string, number>|Record<string, number>} [options.shipmentTotals] - Window → merchandise subtotal (cents)
   * @param {{ min: string, max: string }|null} options.inventoryDateRange - ISO bounds for the multi-apply picker
   */
  init({ dates = new Set(), seasons = [], types = [], inventoryDateRange = null, shipmentTotals = null } = {}) {
    // Persist for updateDateTabs re-init.
    this.#inventoryDateRange = inventoryDateRange;
    this.#seasons            = seasons;
    this.#types              = types;

    const { strings } = window.b2bPlus;
    const tabButtons    = this.#tabButtonsFromDates(dates, shipmentTotals);
    const hasDateTabs   = tabButtons.length > 0;
    const hasDropdowns  = seasons.length > 1 || types.length > 1;
    const scopeOptions  = this.#scopeOptionsFromDates(dates, strings);
    const hasMultiApply = !!(inventoryDateRange?.min && inventoryDateRange?.max);

    // Always tear down flatpickr before any render — including the empty
    // early-return — so a later init cannot destroy() against a detached input
    // or leave an orphaned instance after render(nothing).
    this.#destroyMultiApplyFp();

    if (!hasDateTabs && !hasDropdowns && !hasMultiApply) {
      // Keep lit-html's container state consistent so later init() calls can render reliably.
      this.#safeRender(nothing);
      return;
    }

    this.#safeRender(
      cartHeaderFiltersTemplate({
        tabButtons: hasDateTabs ? tabButtons : null,
        seasons,
        types,
        strings,
        scopeOptions,
        inventoryDateRange,
      }),
    );

    // Paint totals outside lit bindings (see cartDateTabButtonTemplate).
    this.#paintShipmentTotals(shipmentTotals);

    // Events are handled by the delegated listeners in connectedCallback — no per-element binding.
    this.#initMultiApplyFlatpickr(inventoryDateRange);
  }

  /**
   * Rebuild date tabs (and the rest of the filter bar) from a new window set.
   * Always does a full `init()` — partial DOM updates corrupt lit-html's tree.
   * @param {Set<string>} dates
   * @param {Map<string, number>|Record<string, number>|null} [shipmentTotals]
   */
  updateDateTabs(dates = new Set(), shipmentTotals = null) {
    // Always full re-init. Partial DOM surgery (removing `[data-date-tabs]` or
    // rewriting the scope <select> via innerHTML) corrupts lit-html's tree and
    // the next init()/render() throws "Cannot set properties of null (setting 'data')".
    this.init({
      dates,
      seasons: this.#seasons,
      types: this.#types,
      inventoryDateRange: this.#inventoryDateRange,
      shipmentTotals,
    });
  }

  /**
   * Update per-shipment merchandise totals on existing tab buttons without
   * re-rendering (preserves the active tab). Called when quantities change
   * but the set of delivery windows stays the same.
   * @param {Map<string, number>|Record<string, number>|null} shipmentTotals
   */
  updateShipmentTotals(shipmentTotals = null) {
    this.#paintShipmentTotals(shipmentTotals);
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * @param {Set<string>} deliveryWindows
   * @param {Map<string, number>|Record<string, number>|null} [shipmentTotals]
   * @returns {{ value: string, label: string, dateLabel?: string, total?: number, active: boolean }[]}
   * Non-`all` tabs use `label` = "Shipment N" and `dateLabel` = formatted window.
   */
  #tabButtonsFromDates(deliveryWindows, shipmentTotals = null) {
    if (deliveryWindows.size < 1) return [];
    const { strings } = window.b2bPlus;
    const shipmentTpl = strings.shipmentLabel || 'Shipment {n}';
    const getTotal = (win) => {
      if (!shipmentTotals) return 0;
      if (typeof shipmentTotals.get === 'function') return shipmentTotals.get(win) ?? 0;
      return shipmentTotals[win] ?? 0;
    };
    return [
      { value: 'all', label: strings.allDates, active: true },
      ...[...deliveryWindows].sort().map((dw, i) => ({
        value: dw,
        label: shipmentTpl.replace('{n}', String(i + 1)),
        dateLabel: this.#formatDeliveryWindowLabel(dw),
        total: getTotal(dw),
        // Error style on a window whose start has gone by — its lines must be re-dated.
        past: isDeliveryWindowPast(dw),
        active: false,
      })),
    ];
  }

  /** @returns {{ value: string, label: string }[]} */
  #scopeOptionsFromDates(deliveryWindows, strings) {
    const allOption = { value: 'all', label: strings.multiApplyAll ?? 'Apply to all products' };
    if (!deliveryWindows?.size) return [allOption];
    return [
      allOption,
      ...[...deliveryWindows].sort().map((dw) => ({
        value: dw,
        label: this.#formatDeliveryWindowLabel(dw),
      })),
    ];
  }

  /**
   * Tear down the multi-apply flatpickr instance. Safe when the input has
   * already been detached by a prior render(nothing).
   */
  #destroyMultiApplyFp() {
    try {
      this.#multiApplyFp?.destroy();
    } catch (err) {
      console.warn('b2b-plus-cart-header: flatpickr destroy failed', err);
    }
    this.#multiApplyFp = null;
  }

  /**
   * Render into this element. If a prior imperative DOM write corrupted
   * lit-html's markers, wipe the cached part and retry once.
   * @param {unknown} template
   */
  #safeRender(template) {
    try {
      render(template, this);
    } catch (err) {
      console.warn('b2b-plus-cart-header: render failed, resetting lit tree', err);
      try { delete this._$litPart$; } catch { /* ignore */ }
      this.replaceChildren();
      render(template, this);
    }
  }

  /**
   * Write merchandise totals into `[data-shipment-total]` spans (not lit-bound).
   * @param {Map<string, number>|Record<string, number>|null} shipmentTotals
   */
  #paintShipmentTotals(shipmentTotals = null) {
    const getTotal = (win) => {
      if (!shipmentTotals) return 0;
      if (typeof shipmentTotals.get === 'function') return shipmentTotals.get(win) ?? 0;
      return shipmentTotals[win] ?? 0;
    };
    this.querySelectorAll('[data-tab]:not([data-tab="all"])').forEach((btn) => {
      const el = btn.querySelector('[data-shipment-total], .tab-btn-shipment-total');
      if (!el) return;
      el.textContent = formatMoney(getTotal(btn.dataset.tab));
    });
  }

  /**
   * Initialise only the flatpickr instance for the multi-apply date input.
   * Apply-button clicks are handled by the delegated listener in connectedCallback.
   * @param {{ min: string, max: string }|null} inventoryDateRange
   */
  #initMultiApplyFlatpickr(inventoryDateRange) {
    const dateInput = this.querySelector('[data-multi-apply-date]');
    if (!dateInput || !inventoryDateRange?.min) return;

    const { minDays, maxDays: defaultDays } = getShipWindowLengths();
    const singleDayMode = isSingleDayShipWindow();
    const autoHorizonEnd = !singleDayMode && getAtsImmediatePickerHorizonDays() !== null;
    const startOnlyMode = singleDayMode || autoHorizonEnd;
    const inventoryEnd = inventoryDateRange.max ? parseISODateLocal(inventoryDateRange.max) : null;
    const todayISO     = toISODate(startOfLocalDay(new Date()));
    const effectiveMin = inventoryDateRange.min < todayISO ? todayISO : inventoryDateRange.min;

    const { strings } = window.b2bPlus;
    let settling = false;

    const revertPicker = (fp) => {
      const prevStart = dateInput.dataset.selectedStart;
      const prevEnd   = dateInput.dataset.selectedEnd;
      settling = true;
      if (prevStart && prevEnd) {
        if (startOnlyMode) fp.setDate(prevStart, false);
        else fp.setDate([prevStart, prevEnd], false);
      } else {
        fp.clear();
        delete dateInput.dataset.selectedStart;
        delete dateInput.dataset.selectedEnd;
      }
      settling = false;
    };

    const isEndInShipWindow = (selStart, selEnd) => {
      const endDay = startOfLocalDay(selEnd);
      const { minEnd, maxEnd } = shipWindowEndBounds(selStart, minDays, defaultDays, inventoryEnd);
      return endDay >= minEnd && endDay <= maxEnd;
    };

    const shipWindowAlertMessage = () => {
      const base = strings?.dateOutOfRangeError || 'Date not available for this row.';
      if (minDays === defaultDays) {
        return `${base} (${minDays} day${minDays === 1 ? '' : 's'} required.)`;
      }
      return `${base} Select between ${minDays} and ${defaultDays} days.`;
    };

    const commitMultiApply = (selStart, selEnd) => {
      const isoStart = toISODate(selStart);
      const isoEnd   = toISODate(selEnd);
      dateInput.dataset.selectedStart = isoStart;
      dateInput.dataset.selectedEnd   = isoEnd;
      dateInput.value = formatDeliveryWindowDisplay(isoStart, isoEnd);
      const applyBtn = this.querySelector('[data-multi-apply-btn]');
      if (applyBtn) applyBtn.disabled = false;
    };

    const fp = flatpickr(dateInput, {
      mode: startOnlyMode ? 'single' : 'range',
      minDate: effectiveMin,
      maxDate: inventoryDateRange.max ?? undefined,
      // Display-only date rendering; the ISO window the Apply button sends stays
      // in dataset.selectedStart/End (see commitMultiApply).
      ...displayDateFlatpickrOptions(),
      disableMobile: true,
      onChange: (selectedDates) => {
        if (settling) return;

        const applyBtn = this.querySelector('[data-multi-apply-btn]');

        if (startOnlyMode) {
          if (selectedDates.length !== 1) return;
          const day = selectedDates[0];
          const end = autoHorizonEnd
            ? (atsImmediatePickerEnd(inventoryEnd, day) ?? day)
            : day;
          commitMultiApply(day, end);
          return;
        }

        if (selectedDates.length === 1) {
          dateInput.dataset.selectedStart = toISODate(selectedDates[0]);
          delete dateInput.dataset.selectedEnd;
          if (applyBtn) applyBtn.disabled = true;
          return;
        }
        if (selectedDates.length === 2) {
          const selStart = selectedDates[0];
          const selEnd   = selectedDates[1];
          if (!isEndInShipWindow(selStart, selEnd)) {
            window.alert(shipWindowAlertMessage());
            revertPicker(fp);
            return;
          }
          commitMultiApply(selStart, selEnd);
        }
      },
      onClose: (selectedDates) => {
        if (autoHorizonEnd) {
          const s = dateInput.dataset.selectedStart;
          const e = dateInput.dataset.selectedEnd;
          if (s) dateInput.value = formatDeliveryWindowDisplay(s, e || s);
        }
        if (startOnlyMode || minDays !== 1 || settling) return;
        if (selectedDates.length !== 1) return;
        commitMultiApply(selectedDates[0], selectedDates[0]);
      },
    });
    this.#multiApplyFp = fp;
  }

  #updateClearFilterVisibility() {
    const clearButton = this.querySelector('[data-clear-filters]');
    if (!clearButton) return;
    const hasActiveFilter = [...this.querySelectorAll('select:not([data-multi-apply-scope])')].some((s) => s.value !== 'all');
    clearButton.hidden = !hasActiveFilter;
  }

  #dispatchFilterChange() {
    const activeTabButton = this.querySelector('[data-tab].is-active');
    const detail = {
      date:   activeTabButton?.dataset.tab                      ?? 'all',
      season: this.querySelector('[data-filter-season]')?.value ?? 'all',
      type:   this.querySelector('[data-filter-type]')?.value   ?? 'all',
    };
    this.dispatchEvent(new CustomEvent('b2b:cart-filter', {
      bubbles: true,
      detail,
    }));
  }

  /**
   * Formats a raw window string ("2026-05-20 to 2026-05-27") for display as
   * "May 20 to May 27" — the year appears only when it isn't the current one.
   * Display only: `data-tab` and the multi-apply `<option value>` keep the raw
   * ISO window, since both are used as lookup keys.
   *
   * Delegates to the shared formatter so date tabs, the row pickers, and the
   * order summary all read identically.
   */
  #formatDeliveryWindowLabel(deliveryWindowString) {
    return formatDisplayWindowString(deliveryWindowString) || deliveryWindowString;
  }
}

customElements.define('b2b-plus-cart-header', B2BPlusCartHeader);
